"use client";
import React from "react";
import useSWR from "swr";
import { Surcharge } from "@/components/surcharge/surcharge";
import { Category } from "@/dto/category";
import { Menu } from "@/dto/menu";
import { GetMerchantPaymentConfigResponse } from "@/dataLayer/getMerchantPaymentConfig";
import { Item } from "@/dto/item";
import { getItemUrl } from "@/utils/itemUrlUtils";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { useScrollToHash } from "@/utils/useScrollToHash";
import { useTranslations } from "next-intl";
import { ROUTE_SERVICES, ROUTE_GIFTCARD } from "@/utils/constants";
import { MenuCategoriesList } from "@/components/menu/menuCategoriesList";
import { giftCardItem } from "@/utils/giftCardUtils";
import { getServicesMenuDataAction } from "../actions";

interface ServicesMenuItemsProps {
  merchantId: string;
  merchantName: string;
  searchParams: { [key: string]: string | string[] | undefined };
  menu: Menu | null;
  serviceCategories: Category[];
  itemTemplate?: string;
  merchantPaymentConfig: GetMerchantPaymentConfigResponse;
  isSimpleStorefront: boolean;
}

export const ServicesMenuItems = ({
  merchantId,
  merchantName,
  searchParams,
  menu,
  serviceCategories,
  itemTemplate = "description focused",
  merchantPaymentConfig,
  isSimpleStorefront,
}: ServicesMenuItemsProps) => {
  const t = useTranslations("category");
  const query = (searchParams?.query as string) ?? "";

  useScrollToHash(false);

  const { data } = useSWR(
    ["servicesMenuData", merchantId],
    () => getServicesMenuDataAction(merchantId),
    {
      fallbackData: { menu, serviceItems: serviceCategories },
      revalidateOnMount: false,
      revalidateOnFocus: true,
    }
  );
  const activeMenu = data?.menu ?? menu;
  const activeServiceCategories = data?.serviceItems ?? serviceCategories;

  const getItemHref = (item: Item) =>
    item.id === giftCardItem.id
      ? ROUTE_GIFTCARD
      : `${ROUTE_SERVICES}/${getItemUrl(item)}`;

  const shouldShowSurcharge =
    !isSimpleStorefront && Boolean(merchantPaymentConfig?.surcharging?.rate);

  if (!activeMenu) return null;

  return (
    <div
      data-testid={applyDataTestId(pom.landingPage.servicesSection)}
      className="w-full scroll-smooth"
    >
      <div className="sm:hidden text-center">
        {shouldShowSurcharge && (
          <Surcharge surchargeRate={merchantPaymentConfig.surcharging?.rate} />
        )}
      </div>
      <MenuCategoriesList
        menu={activeMenu}
        merchantName={merchantName}
        itemTemplate={itemTemplate}
        query={query}
        serviceCategories={activeServiceCategories}
        getItemHref={getItemHref}
        uncategorizedLabel={t("uncategorized")}
        uncategorizedItemFilter={(item: any) => item.itemType === "SERVICE"}
      />
    </div>
  );
};
